import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

interface SplashScreenProps {
  onComplete: () => void;
}

/**
 * Animated intro splash — white background, scaling logo, pulse rings.
 * Total duration ≤ 2s. Renders as a fixed overlay so the app can pre-load behind it.
 */
export const SplashScreen = ({ onComplete }: SplashScreenProps) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Hold the splash for 1.7s, then fade out for 0.3s → total ~2s
    const hold = setTimeout(() => setVisible(false), 1700);
    return () => clearTimeout(hold);
  }, []);

  return (
    <AnimatePresence onExitComplete={onComplete}>
      {visible && (
        <motion.div
          key="tecly-splash"
          aria-hidden="true"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-white"
        >
          {/* Pulse rings expanding from behind the logo */}
          <div className="relative flex items-center justify-center">
            {[0, 0.4, 0.8].map((delay, i) => (
              <motion.span
                key={i}
                className="absolute h-32 w-32 rounded-full border border-[#3b82f6]/40"
                initial={{ scale: 0.4, opacity: 0.55 }}
                animate={{ scale: 2.6, opacity: 0 }}
                transition={{
                  duration: 1.6,
                  delay,
                  repeat: Infinity,
                  ease: "easeOut",
                }}
              />
            ))}

            {/* Soft radial glow behind logo */}
            <motion.span
              className="absolute h-48 w-48 rounded-full"
              style={{
                background:
                  "radial-gradient(circle, rgba(59,130,246,0.22) 0%, rgba(56,189,248,0.10) 45%, transparent 75%)",
                filter: "blur(20px)",
              }}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1.2, opacity: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            />

            {/* Logo: small → scales up with fade-in */}
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                duration: 0.85,
                ease: [0.34, 1.56, 0.64, 1], // gentle spring-like easing
              }}
              className="relative flex items-baseline"
              style={{
                fontFamily:
                  "-apple-system, 'SF Pro Display', 'Outfit', sans-serif",
                fontWeight: 800,
                fontSize: 56,
                letterSpacing: "-1.5px",
              }}
            >
              <span
                className="mr-1.5 inline-flex items-center justify-center"
                style={{
                  width: "1.15em",
                  height: "1.15em",
                  borderRadius: "22%",
                  background:
                    "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 45%, #38bdf8 100%)",
                  boxShadow:
                    "inset 0 1px 0 rgba(255,255,255,0.35), 0 8px 24px rgba(59,130,246,0.45), 0 2px 6px rgba(56,189,248,0.35)",
                  color: "#ffffff",
                  lineHeight: 1,
                }}
              >
                T
              </span>
              <motion.span
                initial={{ opacity: 0, x: -10, filter: "blur(6px)" }}
                animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                transition={{ duration: 0.5, delay: 0.35, ease: "easeOut" }}
                style={{ color: "#0f172a" }}
              >
                ecly
              </motion.span>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
